import React from "react";
import { createPortal } from "react-dom";

const DEFAULT_WINDOW_FEATURES = {
	width: 1600,
	height: 1000,
	left: 0,
	top: 0,
	resizable: "yes",
	scrollbars: "yes"
};

function buildWindowFeaturesString(features = {}) {
	return Object.entries(features)
		.filter(([, value]) => value !== undefined && value !== null && value !== "")
		.map(([key, value]) => `${key}=${value}`)
		.join(",");
}

function copyDocumentStyles(sourceDocument, targetDocument) {
	if (!sourceDocument || !targetDocument) {
		return;
	}

	const head = targetDocument.head;
	const existingNodes = head.querySelectorAll("[data-popout-cloned='true']");
	existingNodes.forEach((node) => node.parentNode && node.parentNode.removeChild(node));

	Array.from(sourceDocument.querySelectorAll("style, link[rel='stylesheet']")).forEach((node) => {
		const tagName = `${node.tagName || ""}`.toLowerCase();
		if (tagName === "style") {
			const styleNode = targetDocument.createElement("style");
			styleNode.type = "text/css";
			styleNode.textContent = node.textContent || "";
			styleNode.setAttribute("data-popout-cloned", "true");
			head.appendChild(styleNode);
			return;
		}

		if (tagName === "link") {
			const linkNode = targetDocument.createElement("link");
			linkNode.rel = "stylesheet";
			linkNode.href = node.href;
			if (node.media) {
				linkNode.media = node.media;
			}
			linkNode.setAttribute("data-popout-cloned", "true");
			head.appendChild(linkNode);
		}
	});
}

export default class PopoutWindowPortal extends React.PureComponent {
	constructor(props) {
		super(props);
		this.externalWindow = null;
		this.containerElement = null;
		this.isUnmounting = false;
	}

	componentDidMount() {
		if (this.props.open) {
			this.openExternalWindow();
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.open && !prevProps.open) {
			this.openExternalWindow();
		}

		if (!this.props.open && prevProps.open) {
			this.closeExternalWindow(false);
		}

		if (this.props.open && this.externalWindow) {
			this.syncExternalWindowMetadata();
		}
	}

	componentWillUnmount() {
		this.isUnmounting = true;
		this.closeExternalWindow(false);
	}

	getWindowFeatures() {
		const maximizeFeatures = this.props.maximize && typeof window !== "undefined"
			? this.getMaximizedWindowFeatures()
			: {};
		return buildWindowFeaturesString({
			...DEFAULT_WINDOW_FEATURES,
			...maximizeFeatures,
			...(this.props.windowFeatures || {})
		});
	}

	getMaximizedWindowFeatures() {
		if (typeof window === "undefined" || !window.screen) {
			return {};
		}

		const {
			availWidth,
			availHeight,
			availLeft,
			availTop
		} = window.screen;

		return {
			width: availWidth || DEFAULT_WINDOW_FEATURES.width,
			height: availHeight || DEFAULT_WINDOW_FEATURES.height,
			left: Number.isFinite(availLeft) ? availLeft : DEFAULT_WINDOW_FEATURES.left,
			top: Number.isFinite(availTop) ? availTop : DEFAULT_WINDOW_FEATURES.top
		};
	}

	syncExternalWindowMetadata(options = {}) {
		const { copyStyles = false } = options;
		if (!this.externalWindow || this.externalWindow.closed) {
			return;
		}

		const targetDocument = this.externalWindow.document;
		targetDocument.title = this.props.title || document.title || "Canvas";
		targetDocument.documentElement.style.height = "100%";
		targetDocument.documentElement.style.width = "100%";
		targetDocument.body.style.margin = "0";
		targetDocument.body.style.width = "100%";
		targetDocument.body.style.height = "100%";
		targetDocument.body.style.overflow = "hidden";
		targetDocument.body.style.background = this.props.backgroundColor || "#eef2f7";
		targetDocument.documentElement.className = document.documentElement.className;
		targetDocument.body.className = document.body.className;
		if (copyStyles) {
			copyDocumentStyles(document, targetDocument);
		}
	}

	openExternalWindow() {
		if (typeof window === "undefined" || !this.props.open) {
			return;
		}

		if (this.externalWindow && !this.externalWindow.closed && this.containerElement) {
			this.syncExternalWindowMetadata();
			this.externalWindow.focus();
			return;
		}

		const popupWindow = window.open("", this.props.name || "_blank", this.getWindowFeatures());
		if (!popupWindow) {
			if (this.props.onClose && !this.isUnmounting) {
				this.props.onClose();
			}
			return;
		}

		this.externalWindow = popupWindow;
		if (this.props.maximize) {
			const maximizeFeatures = this.getMaximizedWindowFeatures();
			try {
				if (Number.isFinite(maximizeFeatures.left) && Number.isFinite(maximizeFeatures.top)) {
					this.externalWindow.moveTo(maximizeFeatures.left, maximizeFeatures.top);
				}
				if (Number.isFinite(maximizeFeatures.width) && Number.isFinite(maximizeFeatures.height)) {
					this.externalWindow.resizeTo(maximizeFeatures.width, maximizeFeatures.height);
				}
			} catch (err) {
				// Browser window managers may reject move/resize requests; keep the popup open anyway.
			}
		}
		this.externalWindow.document.write("<!DOCTYPE html><html><head></head><body></body></html>");
		this.externalWindow.document.close();
		this.syncExternalWindowMetadata({ copyStyles: true });

		this.containerElement = this.externalWindow.document.createElement("div");
		this.containerElement.style.width = "100%";
		this.containerElement.style.height = "100%";
		this.containerElement.style.minHeight = "100%";
		this.containerElement.style.display = "flex";
		this.containerElement.style.flexDirection = "column";
		this.externalWindow.document.body.appendChild(this.containerElement);

		this.externalWindow.addEventListener("beforeunload", this.handleExternalWindowClose);
		this.externalWindow.addEventListener("unload", this.handleExternalWindowClose);
		this.externalWindow.focus();
		this.forceUpdate();
	}

	closeExternalWindow(notifyParent = false) {
		if (this.externalWindow && !this.externalWindow.closed) {
			this.externalWindow.removeEventListener("beforeunload", this.handleExternalWindowClose);
			this.externalWindow.removeEventListener("unload", this.handleExternalWindowClose);
			this.externalWindow.close();
		}

		this.externalWindow = null;
		this.containerElement = null;

		if (notifyParent && this.props.onClose && !this.isUnmounting) {
			this.props.onClose();
		}
	}

	handleExternalWindowClose = () => {
		this.externalWindow = null;
		this.containerElement = null;
		if (this.props.onClose && !this.isUnmounting) {
			this.props.onClose();
		}
	};

	render() {
		if (!this.props.open || !this.containerElement || !this.externalWindow || this.externalWindow.closed) {
			return null;
		}

		return createPortal(this.props.children, this.containerElement);
	}
}
